<template>
  <div class="salary-root">
    <div class="page">
      <h1 class="title">代付申请</h1>
      <p class="sub">公开页，不登录。填对密钥和代付商户号即可向网关申请出款。配置缓存在本机浏览器。</p>

      <section class="card">
        <h2 class="sec">对接配置</h2>
        <p v-if="restored" class="cache-hint">已从本机恢复上次填写</p>

        <div class="form-item">
          <label class="label" for="mchId">代付商户号</label>
          <div class="control">
            <input id="mchId" v-model.trim="form.mchId" class="input" type="text" inputmode="numeric" maxlength="32" autocomplete="off" placeholder="mchId，8～32 位数字" />
          </div>
        </div>
        <div class="form-item">
          <label class="label" for="secretKey">商户密钥</label>
          <div class="control">
            <div class="secret-row">
              <input
                id="secretKey"
                v-model="form.secretKey"
                class="input"
                :type="showSecret ? 'text' : 'password'"
                autocomplete="off"
                placeholder="开户时下发的 MD5 密钥"
              />
              <button type="button" class="btn btn-primary btn-sm" @click="showSecret = !showSecret">
                {{ showSecret ? '隐藏' : '显示' }}
              </button>
            </div>
          </div>
        </div>
      </section>

      <section class="card">
        <h2 class="sec">出款信息</h2>
        <div class="form-item">
          <label class="label" for="alipayName">收款人姓名</label>
          <div class="control">
            <input id="alipayName" v-model.trim="form.alipayName" class="input" type="text" maxlength="64" autocomplete="off" placeholder="支付宝实名" />
          </div>
        </div>
        <div class="form-item">
          <label class="label" for="alipayAccount">支付宝账号</label>
          <div class="control">
            <input id="alipayAccount" v-model.trim="form.alipayAccount" class="input" type="text" maxlength="128" autocomplete="off" placeholder="登录号或 UID" />
          </div>
        </div>
        <div class="form-item">
          <label class="label" for="amountYuan">代付金额</label>
          <div class="control">
            <input
              id="amountYuan"
              v-model.trim="form.amountYuan"
              class="input"
              :class="{ 'input-err': !!amountLimitError }"
              type="text"
              inputmode="decimal"
              autocomplete="off"
              placeholder="单位：元，单笔须≤500"
            />
            <p class="tip">提交时按文档转成分（整数）。单笔不能超过 500 元。</p>
            <p v-if="amountLimitError" class="field-err">{{ amountLimitError }}</p>
          </div>
        </div>
        <div class="form-item">
          <label class="label" for="outTradeNo">代付订单号</label>
          <div class="control">
            <div class="secret-row">
              <input id="outTradeNo" v-model.trim="form.outTradeNo" class="input" type="text" maxlength="64" autocomplete="off" placeholder="下游唯一单号" />
              <button type="button" class="btn btn-primary btn-sm" @click="refreshOutTradeNo">新单号</button>
            </div>
          </div>
        </div>
        <div class="form-item">
          <label class="label" for="salaryOrderId">平台单号</label>
          <div class="control">
            <input id="salaryOrderId" v-model.trim="form.salaryOrderId" class="input" type="text" autocomplete="off" placeholder="查单用，下单成功后自动回填" />
          </div>
        </div>
      </section>

      <div class="actions">
        <button type="button" class="btn btn-primary" :disabled="busy" @click="onCreate">
          {{ busy === 'create' ? '申请中…' : '申请代付' }}
        </button>
        <button type="button" class="btn btn-success" :disabled="busy" @click="onQuery">
          {{ busy === 'query' ? '查询中…' : '查询订单' }}
        </button>
        <button type="button" class="btn btn-success" :disabled="busy" @click="onBalance">
          {{ busy === 'balance' ? '查询中…' : '查询余额' }}
        </button>
        <button type="button" class="btn btn-primary btn-ghost" :disabled="busy" @click="clearCache">清除本机缓存</button>
      </div>

      <p v-if="errorText" class="err">{{ errorText }}</p>

      <section v-if="summary" class="card result-card">
        <h2 class="sec">返回结果</h2>
        <p class="summary" :class="{ ok: summaryOk, bad: !summaryOk }">{{ summary }}</p>
        <div v-if="resultRows.length" class="kv">
          <div v-for="row in resultRows" :key="row.k" class="kv-row">
            <span>{{ row.k }}</span>
            <span class="kv-val">{{ row.v }}</span>
          </div>
        </div>
        <pre v-if="rawJson" class="raw">{{ rawJson }}</pre>
      </section>
    </div>
  </div>
</template>

<script>
import { postSalaryOpenApi } from '@/api/api'
import {
  salarySign,
  buildSalaryApiUrl,
  formatSalaryTimestamp,
  genSalaryOutTradeNo,
  yuanToFen,
  fenToYuanText,
  assertPayoutFenWithinLimit,
  MAX_PAYOUT_FEN,
  CREATE_SIGN_KEYS,
  QUERY_SIGN_KEYS,
  BALANCE_SIGN_KEYS
} from '@/utils/salarySign'

const PAY_GATEWAY = 'https://pay.jqpay.vip'
const PAY_NOTIFY_URL = 'https://pay.jqpay.vip/api/salary/notify'
const CACHE_KEY = 'sz-cashier-salary-payout'
const CACHE_FIELDS = ['mchId', 'secretKey', 'alipayName', 'alipayAccount', 'amountYuan']
const STYLE_ID = 'salary-payout-stylesheet'
const STATUS_TEXT = {
  0: '代付失败',
  1: '处理中',
  2: '代付成功（已回调）',
  3: '代付成功（未回调）'
}

export default {
  name: 'SalaryPayout',
  data () {
    return {
      form: {
        mchId: '',
        secretKey: '',
        alipayName: '',
        alipayAccount: '',
        amountYuan: '',
        outTradeNo: genSalaryOutTradeNo(),
        salaryOrderId: ''
      },
      showSecret: false,
      restored: false,
      busy: '',
      errorText: '',
      summary: '',
      summaryOk: false,
      resultRows: [],
      rawJson: '',
      persistTimer: null
    }
  },
  computed: {
    amountLimitError () {
      const n = String(this.form.amountYuan || '').trim()
      if (!n) return ''
      try {
        return yuanToFen(n) > MAX_PAYOUT_FEN ? '出款金额不能超过 500 元' : ''
      } catch (e) {
        return ''
      }
    }
  },
  watch: {
    form: {
      deep: true,
      handler () {
        this.schedulePersist()
      }
    }
  },
  created () {
    this.restoreCache()
  },
  mounted () {
    document.title = '代付申请'
    this.ensureStyle()
  },
  beforeDestroy () {
    if (this.persistTimer) {
      clearTimeout(this.persistTimer)
      this.persistTimer = null
    }
    this.persistNow()
  },
  methods: {
    ensureStyle () {
      if (typeof document === 'undefined') return
      let el = document.getElementById(STYLE_ID)
      if (!el) {
        el = document.createElement('style')
        el.id = STYLE_ID
        document.head.appendChild(el)
      }
      el.textContent = `
.salary-root {
  min-height: 100vh; min-height: 100dvh;
  background: #f5f7fa; box-sizing: border-box;
}
.salary-root .page {
  width: 100%; max-width: 720px;
  margin: 0 auto; padding: 24px 16px 48px; box-sizing: border-box;
}
.salary-root .title {
  margin: 0 0 8px; font-size: 22px; font-weight: 600; color: #303133;
}
.salary-root .sub, .salary-root .tip, .salary-root .cache-hint {
  margin: 0 0 16px; font-size: 13px; line-height: 1.5; color: #909399;
}
.salary-root .tip { margin: 6px 0 0; }
.salary-root .cache-hint { color: #67C23A; margin: 0 0 12px; }
.salary-root .card {
  background: #fff; border: 1px solid #ebeef5; border-radius: 8px;
  padding: 16px 16px 8px; margin-bottom: 16px; box-sizing: border-box;
}
.salary-root .sec {
  margin: 0 0 16px; font-size: 16px; font-weight: 600; color: #303133;
}
.salary-root .form-item {
  display: flex; align-items: flex-start; margin-bottom: 16px;
}
.salary-root .label {
  width: 120px; flex-shrink: 0; line-height: 40px; font-size: 14px; color: #606266;
}
.salary-root .control { flex: 1; min-width: 0; }
.salary-root .input {
  width: 100%; height: 40px; box-sizing: border-box; padding: 0 12px;
  border: 1px solid #dcdfe6; border-radius: 4px; font-size: 14px;
  color: #606266; background: #fff;
}
.salary-root .input:focus { outline: none; border-color: #409EFF; }
.salary-root .input-err { border-color: #F56C6C; }
.salary-root .field-err { margin: 6px 0 0; color: #F56C6C; font-size: 13px; line-height: 1.4; }
.salary-root .secret-row { display: flex; gap: 8px; align-items: center; }
.salary-root .secret-row .input { flex: 1; }
.salary-root .actions {
  display: flex; flex-wrap: wrap; gap: 10px; margin: 4px 0 16px;
}
.salary-root .btn {
  height: 40px; padding: 0 18px; border: 0; border-radius: 4px;
  font-size: 14px; cursor: pointer; color: #fff;
}
.salary-root .btn:disabled { opacity: 0.5; cursor: not-allowed; }
.salary-root .btn-primary { background: #409EFF; }
.salary-root .btn-success { background: #67C23A; }
.salary-root .btn-sm { height: 40px; padding: 0 12px; flex-shrink: 0; }
.salary-root .btn-ghost { background: #409EFF; }
.salary-root .err {
  margin: 0 0 12px; color: #F56C6C; font-size: 14px; line-height: 1.5;
}
.salary-root .summary {
  margin: 0 0 12px; font-size: 14px; line-height: 1.5;
}
.salary-root .summary.ok { color: #67C23A; }
.salary-root .summary.bad { color: #F56C6C; }
.salary-root .kv { margin-bottom: 12px; }
.salary-root .kv-row {
  display: flex; justify-content: space-between; gap: 12px;
  font-size: 13px; color: #606266; padding: 6px 0;
  border-bottom: 1px solid #ebeef5;
}
.salary-root .kv-val { color: #303133; word-break: break-all; text-align: right; }
.salary-root .raw {
  margin: 0; padding: 12px; background: #f5f7fa; border-radius: 4px;
  font-size: 12px; line-height: 1.45; overflow: auto; color: #303133;
  white-space: pre-wrap; word-break: break-all;
}
@media (max-width: 520px) {
  .salary-root .form-item { flex-direction: column; }
  .salary-root .label { width: 100%; line-height: 1.4; margin-bottom: 6px; }
  .salary-root .btn { flex: 1 1 calc(50% - 10px); }
}
`
    },
    restoreCache () {
      try {
        const raw = window.localStorage.getItem(CACHE_KEY)
        if (!raw) return
        const saved = JSON.parse(raw)
        if (!saved || typeof saved !== 'object') return
        CACHE_FIELDS.forEach((key) => {
          if (saved[key] != null && String(saved[key]) !== '') {
            this.form[key] = String(saved[key])
          }
        })
        this.restored = true
      } catch (e) {
        /* ignore broken cache */
      }
    },
    schedulePersist () {
      if (this.persistTimer) clearTimeout(this.persistTimer)
      this.persistTimer = setTimeout(() => {
        this.persistTimer = null
        this.persistNow()
      }, 250)
    },
    persistNow () {
      try {
        const payload = {}
        CACHE_FIELDS.forEach((key) => {
          payload[key] = this.form[key]
        })
        window.localStorage.setItem(CACHE_KEY, JSON.stringify(payload))
      } catch (e) {
        /* quota / private mode */
      }
    },
    clearCache () {
      try {
        window.localStorage.removeItem(CACHE_KEY)
      } catch (e) { /* ignore */ }
      this.restored = false
      this.form.mchId = ''
      this.form.secretKey = ''
      this.form.alipayName = ''
      this.form.alipayAccount = ''
      this.form.amountYuan = ''
      this.form.salaryOrderId = ''
      this.form.outTradeNo = genSalaryOutTradeNo()
      this.errorText = ''
      this.summary = '已清除本机缓存'
      this.summaryOk = true
      this.resultRows = []
      this.rawJson = ''
    },
    refreshOutTradeNo () {
      this.form.outTradeNo = genSalaryOutTradeNo()
      this.form.salaryOrderId = ''
    },
    requireConfig () {
      const mchId = String(this.form.mchId || '').trim()
      const secretKey = String(this.form.secretKey || '').trim()
      if (!/^[0-9]{8,32}$/.test(mchId)) throw new Error('代付商户号须为 8～32 位数字')
      if (!secretKey) throw new Error('请填写商户密钥')
      return { mchId, secretKey }
    },
    setResult (ok, summary, data, raw) {
      this.summaryOk = !!ok
      this.summary = summary
      this.rawJson = raw ? JSON.stringify(raw, null, 2) : ''
      this.resultRows = this.buildRows(data)
    },
    buildRows (data) {
      if (!data || typeof data !== 'object') return []
      const order = [
        ['mchId', '代付商户号'],
        ['outTradeNo', '代付订单号'],
        ['salaryOrderId', '平台单号'],
        ['total', '金额(分)'],
        ['status', '状态'],
        ['channelOrderNo', '渠道单号'],
        ['alipay_name', '收款人姓名'],
        ['alipay_account', '支付宝账号'],
        ['availableBalance', '可用余额(分)'],
        ['frozenBalance', '冻结余额(分)'],
        ['currency', '币种'],
        ['succTime', '成功时间'],
        ['notifyTime', '通知时间'],
        ['failReason', '失败原因']
      ]
      const rows = []
      order.forEach(([key, label]) => {
        if (data[key] == null || data[key] === '') return
        let v = String(data[key])
        if (key === 'status') {
          v = v + '（' + (STATUS_TEXT[v] || '未知') + '）'
        }
        if (key === 'total' || key === 'availableBalance' || key === 'frozenBalance') {
          v = v + ' / ' + fenToYuanText(data[key]) + ' 元'
        }
        rows.push({ k: label, v })
      })
      return rows
    },
    unwrapAxios (res) {
      return (res && res.data) ? res.data : res
    },
    axiosErrText (err) {
      if (!err) return '请求失败'
      if (err.message && /network|cors|timeout/i.test(err.message)) return err.message
      const data = err.response && err.response.data
      if (data && (data.message || data.msg)) return String(data.message || data.msg)
      return err.message || err.msg || '请求失败'
    },
    async postSigned (path, payload, secretKey, signKeys) {
      const body = Object.assign({}, payload)
      body.sign = salarySign(body, secretKey, signKeys)
      const url = buildSalaryApiUrl(PAY_GATEWAY, path)
      const res = await postSalaryOpenApi(url, body)
      return this.unwrapAxios(res)
    },
    async onCreate () {
      if (this.busy) return
      this.errorText = ''
      try {
        if (this.amountLimitError) {
          this.errorText = this.amountLimitError
          return
        }
        const { mchId, secretKey } = this.requireConfig()
        const alipayName = String(this.form.alipayName || '').trim()
        const alipayAccount = String(this.form.alipayAccount || '').trim()
        const outTradeNo = String(this.form.outTradeNo || '').trim()
        if (!alipayName) throw new Error('请填写收款人姓名')
        if (!alipayAccount) throw new Error('请填写支付宝账号')
        if (!/^[a-zA-Z0-9_-]{6,64}$/.test(outTradeNo)) throw new Error('代付订单号须为 6～64 位字母数字或 _-')
        const total = assertPayoutFenWithinLimit(yuanToFen(this.form.amountYuan))
        const payload = {
          mchId,
          outTradeNo,
          total,
          notifyUrl: PAY_NOTIFY_URL,
          timestamp: formatSalaryTimestamp(new Date()),
          alipay_name: alipayName,
          alipay_account: alipayAccount
        }
        this.busy = 'create'
        const raw = await this.postSigned('salary', payload, secretKey, CREATE_SIGN_KEYS)
        const ok = raw && String(raw.code) === '0'
        if (ok && raw.data && raw.data.salaryOrderId) {
          this.form.salaryOrderId = String(raw.data.salaryOrderId)
        }
        const failReason = raw && raw.data && raw.data.failReason
        const summary = ok
          ? ('下单已受理' + (failReason ? ('，渠道说明：' + failReason) : '。最终成败请查单或看回调，不要只看本次 HTTP。'))
          : ((raw && (raw.message || raw.msg)) || '下单失败')
        this.setResult(ok, summary, raw && raw.data, raw)
        if (!ok) this.errorText = summary
      } catch (e) {
        this.errorText = this.axiosErrText(e)
        if (this.busy) this.setResult(false, this.errorText, null, null)
      } finally {
        this.busy = ''
      }
    },
    async onQuery () {
      if (this.busy) return
      this.errorText = ''
      try {
        const { mchId, secretKey } = this.requireConfig()
        const outTradeNo = String(this.form.outTradeNo || '').trim()
        const salaryOrderId = String(this.form.salaryOrderId || '').trim()
        if (!outTradeNo && !salaryOrderId) throw new Error('查单请填写代付订单号或平台单号')
        const payload = {
          mchId,
          timestamp: formatSalaryTimestamp(new Date())
        }
        if (outTradeNo) payload.outTradeNo = outTradeNo
        if (salaryOrderId) payload.salaryOrderId = salaryOrderId
        this.busy = 'query'
        const raw = await this.postSigned('salary/query', payload, secretKey, QUERY_SIGN_KEYS)
        const ok = raw && String(raw.code) === '0'
        const st = raw && raw.data && raw.data.status
        const summary = ok
          ? ('查单成功：' + (STATUS_TEXT[String(st)] || ('status=' + String(st || ''))))
          : ((raw && (raw.message || raw.msg)) || '查单失败')
        this.setResult(ok, summary, raw && raw.data, raw)
        if (!ok) this.errorText = summary
      } catch (e) {
        this.errorText = this.axiosErrText(e)
        this.setResult(false, this.errorText, null, null)
      } finally {
        this.busy = ''
      }
    },
    async onBalance () {
      if (this.busy) return
      this.errorText = ''
      try {
        const { mchId, secretKey } = this.requireConfig()
        const payload = {
          mchId,
          timestamp: formatSalaryTimestamp(new Date())
        }
        this.busy = 'balance'
        const raw = await this.postSigned('salary/balance', payload, secretKey, BALANCE_SIGN_KEYS)
        const ok = raw && String(raw.code) === '0'
        const avail = raw && raw.data ? raw.data.availableBalance : null
        const summary = ok
          ? ('可用余额 ' + fenToYuanText(avail) + ' 元')
          : ((raw && (raw.message || raw.msg)) || '余额查询失败')
        this.setResult(ok, summary, raw && raw.data, raw)
        if (!ok) this.errorText = summary
      } catch (e) {
        this.errorText = this.axiosErrText(e)
        this.setResult(false, this.errorText, null, null)
      } finally {
        this.busy = ''
      }
    }
  }
}
</script>
